import { styled, Box, Typography, Button, Chip } from "@mui/material";
import { colors } from "../../theme/colors";

interface StatusChipProps {
  status: "active" | "inactive" | "invited" | "suspended";
}

export const StatusChip = styled(Chip)<StatusChipProps>(({ status }) => {
  const getStatusStyles = () => {
    switch (status) {
      case "active":
        return {
          backgroundColor: "#E8F5E9",
          color: colors.status.active,
          borderColor: colors.status.active,
        };
      case "inactive":
        return {
          backgroundColor: "#F5F5F5",
          color: colors.text.secondary,
          borderColor: colors.text.secondary,
        };
      case "invited":
        return {
          backgroundColor: "#FFF8E1",
          color: "#F57C00",
          borderColor: "#F57C00",
        };
      case "suspended":
        return {
          backgroundColor: "#FFEBEE",
          color: colors.status.suspended,
          borderColor: colors.status.suspended,
        };
      default:
        return {};
    }
  };

  return {
    ...getStatusStyles(),
    fontWeight: 600,
    fontSize: "12px",
    height: "24px",
    borderRadius: "4px",
    border: "1px solid",
  };
});

export const PageContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  padding: "0",
  "& .table-container": {
    padding: 0,
    "& .table-cell": {
      padding: "12px 0 12px 0",
    },
  },
  "& .table-header": {
    padding: "16px 0",
  },
});

export const PageHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
  marginBottom: "32px",
});

export const PageTitleContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
});


export const HeaderActions = styled(Box)({
  display: "flex",
  gap: "12px",
  alignItems: "center",
});

export const CredentialsGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "auto auto",
  gap: "32px 140px",
  marginBottom: "56px",
  justifyContent: "start",
});

export const CredentialItem = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  gap: "24px",
});

export const CredentialLabel = styled(Typography)({
  fontSize: "16px",
  width: "200px",
  color: colors.text.secondary,
  fontFamily: "Nunito",
  flexShrink: 0,
});

export const CredentialValue = styled(Typography)({
  fontSize: "16px",
  color: colors.text.primary,
  fontFamily: "Nunito",
  fontWeight: 400,
  textAlign: "right",
});

export const SectionContainer = styled(Box)({
  backgroundColor: colors.background.secondary,
  borderRadius: "8px",
  padding: "24px",
  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
});

export const SectionHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "16px",
});

export const SectionTitle = styled(Typography)({
  fontSize: "18px",
  fontWeight: 600,
  color: colors.text.primary,
});

export const SectionActions = styled(Box)({
  display: "flex",
  gap: "12px",
  alignItems: "center",
});

export const NoDataText = styled(Typography)({
  fontSize: "14px",
  color: colors.text.secondary,
  textAlign: "center",
  padding: "40px 0",
});

export const EditContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

export const AvatarUploadSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "32px",
  padding: "16px",
  backgroundColor: colors.background.default,
  borderRadius: "16px",
  marginBottom: "32px",
});

export const AvatarEditContainer = styled(Box)({
  position: "relative",
  width: "120px",
  height: "120px",
});

export const AvatarEditOverlay = styled(Box)({
  position: "absolute",
  bottom: "10px",
  left: "50%",
  transform: "translateX(-50%)",
  width: "24px",
  height: "24px",
  borderRadius: "2px",
  backgroundColor: colors.background.secondary,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
});

export const AvatarContainer = styled(Box)({
  position: "relative",
  width: "100%",
  height: "100%",
  borderRadius: "50%",
  overflow: "hidden",
  backgroundColor: colors.background.default,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

export const Avatar = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

export const AvatarPlaceholder = styled(Box)({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: colors.background.mediumBlue,
  color: colors.primary.main,
  fontSize: "48px",
  fontWeight: 600,
});

export const UploadText = styled(Typography)({
  fontSize: "18px",
  fontWeight: 600,
  fontFamily: "Sora",
  color: colors.text.primary,
});

export const FormSection = styled(Box)({
  display: "flex",
  gap: "48px",
});

export const FormColumn = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "12px",
});

export const FormSectionTitle = styled(Typography)({
  fontSize: "18px",
  fontWeight: 600,
  fontFamily: "Sora",
  color: colors.text.primary,
  marginBottom: "4px",
});

export const FormRow = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
});

export const FooterActions = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  gap: "24px",
  marginTop: "32px",
});

export const ResendButton = styled(Button)({
  color: "#4CAF50",
  borderColor: "#4CAF50",
  "&:hover": {
    borderColor: "#388E3C",
    backgroundColor: "rgba(76, 175, 80, 0.04)",
  },
});

export const SuspendButton = styled(Button)({
  backgroundColor: colors.status.error,
  color: "#FFFFFF",
  "&:hover": {
    backgroundColor: "#8B0000",
  },
});

export const ReactivateButton = styled(Button)({
  backgroundColor: colors.status.success,
  color: "#FFFFFF",
  "&:hover": {
    backgroundColor: "#1B5E3B",
  },
});

export const ChangePasswordButton = styled(Button)({
  backgroundColor: colors.amber.main,
  color: "#FFFFFF",
  "&:hover": {
    backgroundColor: colors.amber.dark,
  },
});

export const SearchContainer = styled(Box)({
  display: "flex",
  gap: "16px",
  alignItems: "center",
});
